/**
 * Tesla integration tunables and pure helpers (docs/tesla.md). Dependency-free
 * so the server, the client and the Node tests share them.
 */
import type { TeslaGrant } from "./types";

export const TESLA = {
  /** How long a redeemed Tesla reward keeps the peon as a driver, from the moment they accept the invite. */
  accessHours: 24,
  /** How often the sweep asks Tesla whether an invite has been accepted, at most. */
  pollMs: 5 * 60_000,
  authUrl: "https://auth.tesla.com/oauth2/v3/authorize",
  tokenUrl: "https://fleet-auth.prd.vn.cloud.tesla.com/oauth2/v3/token",
  /** North America fleet region; also the token audience. */
  apiBase: "https://fleet-api.prd.na.vn.cloud.tesla.com",
  scopes: "openid offline_access vehicle_device_data vehicle_cmds",
} as const;

/** The instant access ends for an invite accepted at `acceptedAt`. */
export function accessEndsAt(acceptedAt: Date): string {
  return new Date(acceptedAt.getTime() + TESLA.accessHours * 3_600_000).toISOString();
}

/**
 * Driver ids that appeared since the invite was created: the accepted invitee.
 * The owner and any earlier drivers are in `known`; Tesla's drivers list has
 * no link back to the invite, so a new id is the only signal.
 */
export function newDriverIds(known: ReadonlyArray<string>, current: ReadonlyArray<string>): string[] {
  const seen = new Set(known);
  return current.filter((id) => !seen.has(id));
}

/** Short, human wording for a grant's state on a claim card. */
export function describeGrant(grant: TeslaGrant, now = Date.now()): string {
  switch (grant.status) {
    case "invited": {
      const left = grant.invite_expires_at ? Math.max(0, Math.round((Date.parse(grant.invite_expires_at) - now) / 3_600_000)) : null;
      return left === null ? "Open the link in the Tesla app to accept." : `Open the link in the Tesla app to accept. The invite lasts ${left} more hour${left === 1 ? "" : "s"}.`;
    }
    case "active": {
      const left = grant.access_ends_at ? Math.max(0, Math.round((Date.parse(grant.access_ends_at) - now) / 3_600_000)) : null;
      return left === null ? "You can drive the Tesla." : `You can drive the Tesla for ${left} more hour${left === 1 ? "" : "s"}.`;
    }
    case "ended":
      return "Your turn with the Tesla is over.";
    case "failed":
      return grant.error ? `Tesla access could not be set up: ${grant.error}` : "Tesla access could not be set up.";
  }
}
